import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ApplicationRef,
  ErrorHandler,
  InjectionToken,
  Injector,
  NgModule,
  PLATFORM_ID,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  initializeApp as initializeFireApp,
  provideFirebaseApp,
} from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import {
  provideClientHydration,
  withI18nSupport,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import {
  ServiceWorkerModule,
  SwRegistrationOptions,
} from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { Auth } from '@freelancer/auth';
// Deep imports to enable better tree-shaking
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { FakeCollectionsWrapperComponent } from '@freelancer/datastore/collections-testing/fake-collections-widget/fake-collections-wrapper.component';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { FakeCollectionsWrapperTestingComponent } from '@freelancer/datastore/collections-testing/fake-collections-widget/testing/fake-collections-wrapper-testing.component';
import { SSR_MAPS_API_KEY } from '@freelancer/google-maps';
import { PwaInitialState } from '@freelancer/initial-state';
import { LocationInitialUrl, LocationModule } from '@freelancer/location';
import {
  LOGIN_AUTH_SERVICE,
  LOGIN_REDIRECT_SERVICE,
  Login,
  LoginModule,
} from '@freelancer/login';
import { NetworkAlertService, NetworkModule } from '@freelancer/network';
import { NotificationsModule } from '@freelancer/notifications';
import { PWA_CONFIG, PwaModule } from '@freelancer/pwa';
import { SeoModule } from '@freelancer/seo';
import { ThemeComponent } from '@freelancer/theme';
import {
  ErrorTrackingGlobalAccess,
  FlTraceService,
} from '@freelancer/tracking';
import { UpdateNativeAlertModule } from '@freelancer/ui/update-native-alert';
import { UserAgent } from '@freelancer/user-agent';
import { toNumber } from '@freelancer/utils';
import 'capacitor-plugin-service-worker';
import { NEVER } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { AppCommonModule } from './app-common.module';
import { environment } from '../environments/environment';
import { ShellRoutingModule } from './app-shell/shell-routing.module';
import { AppComponent } from './app.component';
import {
  parseExperimentalSwUserIds,
  shouldUseExperimentalSw,
} from './experimental-service-worker';
import { ToastAlertsModule } from './toast-alerts/toast-alerts.module';

// User IDs for experimental lazy SW caching test, configured per environment.
// Remove once testing is complete.
const EXPERIMENTAL_SW_USER_IDS = parseExperimentalSwUserIds(
  environment.experimentalServiceWorkerUserIds,
);

// YOU ARE PROBABLY LOOKING FOR app-common.module.ts !!!

// /!\ DO NOT ADD ANYTHING IN THERE WITHOUT TALKING TO FRONTEND INFRA FIRST /!\
// Application-wide code should be avoided as much as possible; does your code
// needs:
// - to be loaded on ALL logged-in pages?
// - AND to be loaded on ALL logged-out pages?
// - AND to be loaded on the Admin?
// - ...
// Probably not.

const CORE_WEB_VITALS_TRACKING = new InjectionToken<LocationInitialUrl>(
  'LocationInitialUrl',
);
const PWA_INITIAL_STATE = new InjectionToken<PwaInitialState>(
  'PwaInitialState',
);

export function initializeApp(
  platformId: object,
  locationInitialUrl: LocationInitialUrl,
): () => void {
  return () => {
    if (isPlatformBrowser(platformId)) {
      // Store original url before internal routing happens
      locationInitialUrl.init();

      // Mark the app as bootstrapped for inline scripts
      // eslint-disable-next-line no-restricted-globals
      if (window.webapp) {
        // eslint-disable-next-line no-restricted-globals
        window.webapp.applicationBootstrapped = true;
      }
    }
  };
}

@NgModule({
  imports: [
    AppCommonModule,
    ShellRoutingModule,
    LocationModule,
    LoginModule,
    SeoModule,
    PwaModule,
    UpdateNativeAlertModule,
    NetworkModule,
    ToastAlertsModule,
    ServiceWorkerModule.register('ngsw-worker.js'),
    NotificationsModule,
    ThemeComponent,
    environment.useFakes
      ? FakeCollectionsWrapperComponent
      : FakeCollectionsWrapperTestingComponent,
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: SwRegistrationOptions,
      deps: [PLATFORM_ID, Auth, Injector, UserAgent],
      useFactory: (
        platformId: object,
        auth: Auth,
        injector: Injector,
        userAgent: UserAgent,
      ) => ({
        /* eslint-disable no-restricted-properties, no-restricted-globals */
        enabled:
          environment.production &&
          isPlatformBrowser(platformId) &&
          // disable on Edge Legacy due to prefetching issues
          // https://github.com/angular/angular/issues/27261
          userAgent.getUserAgent().getEngine()?.name !== 'EdgeHTML' &&
          // Special conditions for Android Capacitor
          // where we don't want to enable the service worker.
          //  * Disable on Android WebView 90 as the Chromium team broke it
          //    Ref: https://bugs.chromium.org/p/chromium/issues/detail?id=1204098
          //  * T242019: Disable on Android OS version lower than 7.0
          //    because it doesn't support Android ServiceWorkerController.
          //
          // Note: we use the Capacitor API directly here as the Pwa service
          // injects ApplicationRef, which is not available at that point.
          (Capacitor.getPlatform() !== 'android' ||
            (!userAgent.getUserAgent().getBrowser().version?.startsWith('90') &&
              (toNumber(userAgent.getUserAgent().getOS().version) ?? 0) >=
                /* Nougat */ 7)) &&
          // Also disable the service worker for iOS 17.0 and 17.1
          // because a bug in WebKit causes the service worker cache to break.
          // This affects Capacitor and ALL iOS browsers (inc. "Chrome")
          // Ref: https://github.com/angular/angular/issues/50378
          // allow if not iOS
          (userAgent.getUserAgent().getOS().name !== 'iOS' ||
            // allow if not one of the problematic versions
            (!userAgent.getUserAgent().getOS().version?.startsWith('17.0') &&
              !userAgent.getUserAgent().getOS().version?.startsWith('17.1'))) &&
          // Setting a ?sw=false query param or DISABLE_NGSW=true cookie
          // disables the SW to allow testing without the Service Worker
          // running.
          !window.location.search.includes('sw=false') &&
          !window.document.cookie.includes('DISABLE_NGSW=true'),
        /* eslint-enable no-restricted-properties, no-restricted-globals */
        registrationStrategy: () =>
          // Register the SW when the user logs in & the application is idle.
          // Experimental users get a lazy-caching variant of the SW for
          // testing — remove EXPERIMENTAL_SW_USER_IDS once testing is done.
          auth.isLoggedIn().pipe(
            filter(isLoggedIn => isLoggedIn),
            switchMap(() => auth.getUserId().pipe(first())),
            switchMap(userId => {
              /* eslint-disable no-restricted-properties, no-restricted-globals */
              if (
                shouldUseExperimentalSw({
                  userId,
                  experimentalUserIds: EXPERIMENTAL_SW_USER_IDS,
                  cookies: window.document.cookie,
                  // The cookie opt-in is for QA & e2e only — never in prod,
                  // where exposure is only ever the hardcoded ID list, and
                  // the experiment only runs on the main app.
                  allowCookieOptIn:
                    environment.appName === 'main' &&
                    environment.environmentName !== 'prod',
                })
              ) {
                // Respect the DISABLE_NGSW=true cookie kill switch that
                // `enabled` above uses for the normal flow.
                if (window.document.cookie.includes('DISABLE_NGSW=true')) {
                  return NEVER;
                }
                navigator.serviceWorker
                  .register('/ngsw-worker-experimental.js', { scope: '/' })
                  .catch((err: unknown) => {
                    injector
                      .get(ErrorHandler)
                      .handleError(
                        new Error(
                          `Experimental SW registration failed: ${String(err)}`,
                        ),
                      );
                  });
                // Return NEVER so ServiceWorkerModule does not also register
                // ngsw-worker.js for these users.
                return NEVER;
              }
              /* eslint-enable no-restricted-properties, no-restricted-globals */

              return injector
                .get(ApplicationRef)
                .isStable.pipe(filter(isStable => isStable));
            }),
          ),
        scope: '/',
      }),
    },
    provideFirebaseApp(() =>
      initializeFireApp({
        apiKey: environment.firebase.apiKey,
        projectId: environment.firebase.projectId,
        messagingSenderId: environment.firebase.messagingSenderId,
        appId: environment.firebase.appId,
      }),
    ),
    provideMessaging(() => getMessaging()),
    {
      provide: LOGIN_REDIRECT_SERVICE,
      useExisting: Login,
    },
    {
      provide: LOGIN_AUTH_SERVICE,
      useExisting: Auth,
    },
    {
      provide: CORE_WEB_VITALS_TRACKING,
      useExisting: LocationInitialUrl,
    },
    {
      provide: PWA_INITIAL_STATE,
      useExisting: PwaInitialState,
    },
    provideAppInitializer(() => {
      inject(DOCUMENT);
      const platformId = inject(PLATFORM_ID);
      const locationInitialUrl = inject(CORE_WEB_VITALS_TRACKING);
      inject(PWA_INITIAL_STATE);
      inject(FlTraceService);
      inject(ErrorTrackingGlobalAccess);
      inject(NetworkAlertService);

      initializeApp(platformId, locationInitialUrl);
    }),
    {
      provide: PWA_CONFIG,
      useValue: { appIcon: environment.pwaConfig.appIcon },
    },
    {
      provide: SSR_MAPS_API_KEY,
      deps: [PLATFORM_ID],
      useFactory: (platformId: string) => {
        if (isPlatformServer(platformId)) {
          return process.env.SSR_MAPS_API_KEY ?? environment.mapsConfig.apiKey;
        }

        return undefined;
      },
    },
    provideClientHydration(withI18nSupport(), withNoHttpTransferCache()),
  ],
})
export class AppModule {}
